<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import Swal from 'sweetalert2';
import { currentUser, updateUser } from '../db/auth.js';
import ChatAssistant from "../components/ChatAssistant.vue";

const user = ref(currentUser());
const form = ref({ full_name: user.value?.full_name || '', email: user.value?.email || '', avatar_url: user.value?.avatar_url || null, bg_category: user.value?.bg_category || 'nature' });
const bgUrl = ref(null);
const showEdit = ref(false);
const isUploading = ref(false);

let rotateTimer = null;
const ROTATE_MS = 22000;

function setRandomBackground() {
  const cat = form.value.bg_category || 'nature';
  const url = `https://loremflickr.com/1600/600/${encodeURIComponent(cat)}?random=${Math.floor(Math.random()*1e9)}`;
  const img = new Image();
  img.onload = () => { bgUrl.value = url; };
  img.src = url;
}

onMounted(() => {
  user.value = currentUser();
  form.value = { full_name: user.value?.full_name || '', email: user.value?.email || '', avatar_url: user.value?.avatar_url || null, bg_category: user.value?.bg_category || 'nature' };
  if (typeof window !== 'undefined') {
    window.addEventListener('session-updated', () => {
      user.value = currentUser();
      form.value = { full_name: user.value?.full_name || '', email: user.value?.email || '', avatar_url: user.value?.avatar_url || null, bg_category: user.value?.bg_category || 'nature' };
    });
  }
  setRandomBackground();
  rotateTimer = setInterval(setRandomBackground, ROTATE_MS);
});

onUnmounted(() => { if (rotateTimer) clearInterval(rotateTimer); });

const initials = computed(() => {
  const base = (user.value?.full_name || user.value?.username || 'U').trim();
  const parts = base.split(/\s+/).filter(Boolean);
  const first = (parts[0] && parts[0][0]) || 'U';
  const second = (parts.length > 1 && parts[1] && parts[1][0]) || '';
  return (first + second).toUpperCase();
});

const roleColor = computed(() => {
  const role = (user.value?.role || '').toLowerCase();
  const colors = {
    admin: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    mesero: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    chef: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    proveedor: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    cliente: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
  };
  return colors[role] || colors.cliente;
});

async function onAvatarChange(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  if (file.size > 1024 * 1024 * 2) {
    Swal.fire({ icon: 'warning', title: 'Imagen muy grande', text: 'Máximo 2MB.' });
    return;
  }
  isUploading.value = true;
  const reader = new FileReader();
  reader.onload = () => { 
    form.value.avatar_url = reader.result;
    isUploading.value = false;
  };
  reader.readAsDataURL(file);
}

async function save() {
  if (!user.value) return;
  const { full_name, email, avatar_url, bg_category } = form.value;
  const res = await updateUser(user.value.id, { full_name, email, avatar_url, bg_category });
  if (res.ok) {
    user.value = currentUser();
    Swal.fire({ 
      icon: 'success', 
      title: 'Perfil actualizado',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true
    });
  } else {
    Swal.fire({ icon: 'error', title: 'Error', text: res.message || 'No se pudo actualizar' });
  }
}

function resetForm() {
  form.value = { full_name: user.value?.full_name || '', email: user.value?.email || '', avatar_url: user.value?.avatar_url || null, bg_category: user.value?.bg_category || 'nature' };
}

function openEdit() { 
  showEdit.value = true;
  document.body.style.overflow = 'hidden';
}
function closeEdit() { 
  showEdit.value = false;
  document.body.style.overflow = '';
}
async function saveAndClose() { 
  await save(); 
  closeEdit();
}
</script>

<template>
  <section class="profile-shell">
    <div class="hero">
      <div class="hero-bg" aria-hidden="true"></div>
      <div v-if="bgUrl" class="hero-image" :style="{ backgroundImage: `url(${bgUrl})` }" aria-hidden="true"></div>
      <div class="hero-overlay" aria-hidden="true"></div>
      <div class="hero-content">
        <div class="avatar-wrap">
          <div class="avatar-container">
            <img v-if="form.avatar_url" :src="form.avatar_url" alt="avatar" class="avatar" />
            <div v-else class="avatar fallback">{{ initials }}</div>
            <div v-if="isUploading" class="upload-overlay">
              <div class="spinner"></div>
            </div>
            <button type="button" class="edit-avatar" @click="openEdit" aria-label="Editar perfil">
              <span class="material-symbols-rounded">edit</span>
            </button>
          </div>
        </div>
        <h2 class="name">{{ user?.full_name || user?.username }}</h2>
        <p class="meta">
          <span class="chip" :style="{ background: roleColor }">
            <span class="material-symbols-rounded">badge</span>
            {{ (user?.role || '').toUpperCase() }}
          </span>
          <span v-if="user?.email" class="chip email">
            <span class="material-symbols-rounded">email</span>
            {{ user.email }}
          </span>
        </p>
        <div class="stats-grid">
          <div class="stat-card stat-card-blue">
            <div class="stat-icon">
              <span class="material-symbols-rounded">person</span>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ user?.username || 'N/A' }}</div>
              <div class="stat-label">Usuario</div>
            </div>
          </div>
          <div class="stat-card stat-card-green">
            <div class="stat-icon">
              <span class="material-symbols-rounded">verified</span>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ user?.is_verified ? 'Verificado' : 'Pendiente' }}</div>
              <div class="stat-label">Estado</div>
            </div>
          </div>
          <div class="stat-card stat-card-orange">
            <div class="stat-icon">
              <span class="material-symbols-rounded">calendar_today</span>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ user?.created_at ? new Date(user.created_at).toLocaleDateString('es-ES', { month: 'short', year: 'numeric' }) : 'N/A' }}</div>
              <div class="stat-label">Miembro desde</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de edición -->
    <Transition name="modal">
      <div v-if="showEdit" class="modal-backdrop" @click.self="closeEdit">
        <div class="modal" role="dialog" aria-modal="true" aria-label="Editar perfil">
          <div class="modal-header">
            <h3>
              <span class="material-symbols-rounded">edit</span>
              Editar perfil
            </h3>
            <button type="button" class="close-btn" @click="closeEdit" aria-label="Cerrar">
              <span class="material-symbols-rounded">close</span>
            </button>
          </div>
          <form class="edit-form" @submit.prevent="saveAndClose">
            <div class="form-section">
              <h4 class="section-title">
                <span class="material-symbols-rounded">image</span>
                Foto de perfil
              </h4>
              <div class="avatar-upload">
                <div class="avatar-preview">
                  <img v-if="form.avatar_url" :src="form.avatar_url" alt="avatar" class="preview-avatar" />
                  <div v-else class="preview-avatar fallback">{{ initials }}</div>
                  <div v-if="isUploading" class="upload-overlay">
                    <div class="spinner"></div>
                  </div>
                </div>
                <label class="upload-label">
                  <input type="file" accept="image/*" @change="onAvatarChange" />
                  <span class="material-symbols-rounded">camera_alt</span>
                  <span>Cambiar foto</span>
                </label>
              </div>
            </div>

            <div class="form-section">
              <h4 class="section-title">
                <span class="material-symbols-rounded">person</span>
                Información personal
              </h4>
              <div class="field">
                <label>
                  <span class="material-symbols-rounded">badge</span>
                  Nombre completo
                </label>
                <input v-model="form.full_name" type="text" placeholder="Tu nombre completo" />
              </div>
              <div class="field">
                <label>
                  <span class="material-symbols-rounded">email</span>
                  Email
                </label>
                <input v-model="form.email" type="email" placeholder="tucorreo@dominio.com" />
              </div>
            </div>

            <div class="form-section">
              <h4 class="section-title">
                <span class="material-symbols-rounded">palette</span>
                Personalización
              </h4>
              <div class="field">
                <label>
                  <span class="material-symbols-rounded">image</span>
                  Categoría de fondo
                </label>
                <select v-model="form.bg_category" class="select-modern">
                  <option value="nature">🌿 Naturaleza</option>
                  <option value="forest">🌲 Bosque</option>
                  <option value="mountain">⛰️ Montaña</option>
                  <option value="ocean">🌊 Océano</option>
                  <option value="river">🏞️ Río</option>
                  <option value="animals">🐾 Animales</option>
                  <option value="city">🏙️ Ciudad</option>
                  <option value="travel">✈️ Viajes</option>
                </select>
              </div>
            </div>

            <div class="actions">
              <button type="button" class="btn ghost" @click="closeEdit(); resetForm()">
                <span class="material-symbols-rounded">close</span>
                Cancelar
              </button>
              <button type="submit" class="btn primary">
                <span class="material-symbols-rounded">save</span>
                Guardar cambios
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
    <ChatAssistant />
  </section>
</template>

<style scoped>
.profile-shell {
  min-height: calc(100vh - 70px);
  padding: 1.4rem;
  max-width: 1400px;
  margin: 0 auto;
}

/* Hero Section */
.hero {
  position: relative;
  border-radius: 32px;
  overflow: hidden;
  margin-bottom: 2rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.hero:hover {
  transform: translateY(-4px);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.2);
}

.hero-bg {
  position: absolute;
  inset: -40% -20% auto -20%;
  height: 500px;
  background: radial-gradient(120px 120px at 40% 30%, rgba(198, 146, 61, 0.12), transparent),
              radial-gradient(220px 220px at 60% 70%, rgba(0, 194, 168, 0.1), transparent),
              radial-gradient(160px 160px at 50% 50%, rgba(255, 149, 0, 0.08), transparent);
  filter: blur(40px) saturate(120%);
  animation: gradientShift 8s ease infinite;
}

@keyframes gradientShift {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(20px, -20px) scale(1.1); }
}

.hero-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: saturate(150%) contrast(110%) brightness(1.05);
  opacity: 1;
  transition: filter 0.6s ease, opacity 0.6s ease, transform 0.6s ease;
  animation: imageFade 0.6s ease;
}

@keyframes imageFade {
  from { opacity: 0; transform: scale(1.1); }
  to { opacity: 1; transform: scale(1); }
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%);
  backdrop-filter: blur(1px);
}

.hero-content {
  position: relative;
  z-index: 1;
  padding: 4rem 2rem 3rem;
  display: grid;
  justify-items: center;
  gap: 1.2rem;
}

/* Avatar */
.avatar-wrap {
  position: relative;
}

.avatar-container {
  position: relative;
  display: inline-block;
}

.avatar {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 20px 60px rgba(198, 146, 61, 0.4),
              0 0 0 8px rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: avatarFloat 3s ease-in-out infinite;
}

@keyframes avatarFloat {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
}

.avatar:hover {
  transform: scale(1.05) translateY(-4px);
  box-shadow: 0 24px 80px rgba(198, 146, 61, 0.5),
              0 0 0 12px rgba(255, 255, 255, 0.15);
}

.avatar.fallback {
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 2rem;
  background: linear-gradient(135deg, #C6923D 0%, #00c2a8 100%);
  color: white;
  box-shadow: 0 20px 60px rgba(198, 146, 61, 0.4);
}

.upload-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: grid;
  place-items: center;
  backdrop-filter: blur(4px);
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.edit-avatar {
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: #C6923D;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  z-index: 2;
  transition: all 0.3s ease;
}

.edit-avatar:hover {
  transform: scale(1.1) rotate(90deg);
  background: white;
  box-shadow: 0 12px 32px rgba(198, 146, 61, 0.4);
}

.edit-avatar .material-symbols-rounded {
  font-size: 20px;
}

/* Name and Meta */
.name {
  font-size: 2.2rem;
  font-weight: 800;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  margin: 0;
  letter-spacing: -0.5px;
}

.meta {
  display: inline-flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  justify-content: center;
}

.chip {
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  background: rgba(255, 255, 255, 0.25);
}

.chip .material-symbols-rounded {
  font-size: 16px;
}

.chip.email {
  background: rgba(255, 255, 255, 0.2);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  width: 100%;
  max-width: 800px;
  margin-top: 1rem;
}

.stat-card {
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0.95;
  z-index: 0;
}

.stat-card-blue {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.stat-card-blue::before {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.stat-card-green {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.stat-card-green::before {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.stat-card-orange {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
}

.stat-card-orange::before {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
}

.stat-card:hover {
  transform: translateY(-6px) scale(1.02);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.3);
}

.stat-card-blue:hover {
  box-shadow: 0 16px 40px rgba(79, 172, 254, 0.5);
}

.stat-card-green:hover {
  box-shadow: 0 16px 40px rgba(67, 233, 123, 0.5);
}

.stat-card-orange:hover {
  box-shadow: 0 16px 40px rgba(250, 112, 154, 0.5);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(10px);
  display: grid;
  place-items: center;
  color: white;
  font-size: 28px;
  position: relative;
  z-index: 1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.stat-info {
  flex: 1;
  position: relative;
  z-index: 1;
}

.stat-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.2rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: grid;
  place-items: center;
  z-index: 5000;
  padding: 1rem;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal {
  width: min(90vw, 700px);
  max-height: 90vh;
  overflow-y: auto;
  background: var(--panel-bg);
  color: var(--text);
  border: 1px solid var(--panel-border);
  border-radius: 24px;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.4);
  animation: modalSlide 0.3s ease;
  position: relative;
}

@keyframes modalSlide {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid var(--panel-border);
  position: sticky;
  top: 0;
  background: var(--panel-bg);
  z-index: 10;
  backdrop-filter: blur(10px);
}

.modal-header h3 {
  margin: 0;
  font-weight: 800;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.modal-header .material-symbols-rounded {
  font-size: 24px;
  color: var(--accent);
}

.close-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: none;
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: var(--accent);
  color: white;
  transform: rotate(90deg);
}

.edit-form {
  padding: 2rem;
  display: grid;
  gap: 2rem;
}

.form-section {
  display: grid;
  gap: 1.2rem;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding-bottom: 0.8rem;
  border-bottom: 2px solid var(--panel-border);
}

.section-title .material-symbols-rounded {
  font-size: 20px;
  color: var(--accent);
}

/* Avatar Upload */
.avatar-upload {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2rem;
}

.avatar-preview {
  position: relative;
}

.preview-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--panel-border);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.preview-avatar.fallback {
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 2rem;
  background: linear-gradient(135deg, #C6923D 0%, #00c2a8 100%);
  color: white;
}

.upload-label {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 1.4rem;
  border-radius: 12px;
  background: var(--surface);
  border: 2px dashed var(--panel-border);
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  color: var(--text);
}

.upload-label:hover {
  background: var(--accent);
  color: white;
  border-color: var(--accent);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(198, 146, 61, 0.3);
}

.upload-label input {
  display: none;
}

.upload-label .material-symbols-rounded {
  font-size: 20px;
}

/* Fields */
.field {
  display: grid;
  gap: 0.6rem;
}

.field label {
  font-weight: 600;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
}

.field label .material-symbols-rounded {
  font-size: 18px;
  color: var(--accent);
}

.field input,
.field select {
  padding: 0.85rem 1rem;
  border-radius: 12px;
  border: 2px solid var(--panel-border);
  background: var(--surface);
  color: var(--text);
  font-size: 1rem;
  transition: all 0.3s ease;
}

.field input:focus,
.field select:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 4px rgba(198, 146, 61, 0.1);
}

.select-modern {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.8rem center;
  background-size: 20px;
  padding-right: 3rem;
}

.field select option {
  background: var(--panel-bg);
  color: var(--text);
  padding: 0.5rem;
}

/* Actions */
.actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid var(--panel-border);
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  border: 2px solid transparent;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn .material-symbols-rounded {
  font-size: 20px;
}

.btn.ghost {
  background: var(--surface);
  color: var(--text);
  border-color: var(--panel-border);
}

.btn.ghost:hover {
  background: var(--panel-bg);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn.primary {
  background: linear-gradient(135deg, #C6923D 0%, #00c2a8 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 16px rgba(198, 146, 61, 0.4);
}

.btn.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(198, 146, 61, 0.5);
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .profile-shell {
    padding: 1rem;
  }

  .hero-content {
    padding: 3rem 1.5rem 2rem;
  }

  .avatar {
    width: 120px;
    height: 120px;
  }

  .name {
    font-size: 1.8rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
    gap: 0.8rem;
  }

  .stat-card {
    padding: 1.2rem;
  }

  .modal {
    width: 95vw;
    border-radius: 20px;
  }

  .modal-header {
    padding: 1.2rem 1.5rem;
  }

  .edit-form {
    padding: 1.5rem;
    gap: 1.5rem;
  }

  .actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .hero-content {
    padding: 2.5rem 1rem 1.5rem;
  }

  .avatar {
    width: 100px;
    height: 100px;
  }

  .name {
    font-size: 1.5rem;
  }

  .chip {
    font-size: 0.75rem;
    padding: 0.4rem 0.8rem;
  }
}
</style>
